/**
 * @file index.tsx
 *
 * @fileoverview Shows a text next to an image.
 */

import { cva, VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import Figure from "../../../02-atoms/30-figure";
import styles from "./sideText.module.scss";

const sideWrapper = cva([styles.sideWrapper], {
  variants: {
    imageOrientation: {
      left: styles.sideWrapperImageLeft,
      right: styles.sideWrapperImageRight,
    },
  },
  defaultVariants: {
    imageOrientation: "left",
  },
});

const sideText = cva([styles.sideText], {
  variants: {
    imageOrientation: {
      left: styles.sideTextImageLeft,
      right: styles.sideTextImageRight,
    },
  },
  defaultVariants: {
    imageOrientation: "left",
  },
});

export interface SideTextProps extends VariantProps<typeof sideWrapper> {
  /**
   * The image to show next to the text.
   */
  image: {
    url: string;
    alt: string;
    title: string;
    width?: string;
    height?: string;
  };
  /**
   * The image caption.
   */
  caption?: string;
  /**
   * The text to show.
   */
  children: ReactNode;
}

function ImageWrapper({
  caption,
  children,
}: Pick<SideTextProps, "caption"> & {
  children: ReactNode;
}) {
  return caption ? (
    <Figure caption={caption} className={styles.sideImage}>
      {children}
    </Figure>
  ) : (
    <>{children}</>
  );
}

/**
 * Provides an Image with text o side. The image can
 * be aligned to left or right of the text by using the
 * "imageOrientation" prop.
 */
export const SideText = ({
  image,
  imageOrientation = "left",
  children,
  caption,
  ...props
}: SideTextProps) => {
  return (
    <div className={sideWrapper({ imageOrientation })}>
      <ImageWrapper caption={caption}>
        <img
          className={caption ? "" : styles.sideImage}
          src={image.url}
          alt={image.alt}
          title={image.title}
          width={image.width ?? "auto"}
          height={image.height ?? "auto"}
          {...props}
        />
      </ImageWrapper>
      <div className={sideText({ imageOrientation })}>{children}</div>
    </div>
  );
};

export default SideText;
